/**
 * The display pass: an optional palette quantizer.
 *
 * Two details carry the aesthetic, and the second is the one usually missed.
 *
 * The palette snap is straightforward: for each pixel, find the nearest of the
 * ~48 generated entries. Forty-eight iterations at this resolution is nothing.
 *
 * The **Bayer threshold is anchored to the internal-target pixel grid**, which
 * `gl_FragCoord` gives directly — but that only produces stable dithering
 * because the camera translates in whole internal pixels (see
 * `viewport.ts`). World features therefore move by whole pixels under a pan,
 * the dither pattern stays registered to them, and the image does not crawl.
 * Anchor the threshold to anything else, or let the camera move sub-pixel, and
 * the output reads as "low-res 3D" rather than as pixel art.
 */

import {
  DataTexture,
  FloatType,
  GLSL3,
  Mesh,
  NearestFilter,
  OrthographicCamera,
  PlaneGeometry,
  RGBAFormat,
  RawShaderMaterial,
  Scene,
  Texture,
  Vector2,
} from 'three'
import type { Palette } from '../forge/palette.js'

const VERT = /* glsl */ `
in vec3 position;
in vec2 uv;
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`

const FRAG = /* glsl */ `
precision highp float;
precision highp int;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D tScene;
uniform sampler2D tPalette;
uniform float uPaletteSize;
uniform float uDitherAmount;
uniform float uEnabled;

// 4x4 Bayer matrix, normalized to [-0.5, 0.5].
const float bayer[16] = float[16](
   0.0,  8.0,  2.0, 10.0,
  12.0,  4.0, 14.0,  6.0,
   3.0, 11.0,  1.0,  9.0,
  15.0,  7.0, 13.0,  5.0
);

void main() {
  vec2 uv = vUv;
  vec3 c = texture(tScene, uv).rgb;

  if (uEnabled < 0.5) {
    fragColor = vec4(c, 1.0);
    return;
  }

  // Anchored to gl_FragCoord, which is the internal-target pixel grid. The
  // camera moves in whole target pixels, so this grid stays registered to
  // world features across a pan and the dither does not crawl.
  ivec2 p = ivec2(gl_FragCoord.xy) & ivec2(3);
  float threshold = (bayer[p.y * 4 + p.x] / 16.0) - 0.46875;
  c += threshold * uDitherAmount;

  // Nearest palette entry, weighted toward luminance so the snap follows
  // perceived brightness rather than raw channel distance.
  float best = 1e9;
  vec3 bestColor = vec3(0.0);
  for (int i = 0; i < 256; i++) {
    if (float(i) >= uPaletteSize) break;
    vec3 e = texelFetch(tPalette, ivec2(i, 0), 0).rgb;
    vec3 d = e - c;
    float dist = d.r * d.r * 3.0 + d.g * d.g * 6.0 + d.b * d.b;
    if (dist < best) {
      best = dist;
      bestColor = e;
    }
  }
  fragColor = vec4(bestColor, 1.0);
}
`

export class PixelPass {
  readonly scene = new Scene()
  readonly camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)
  readonly material: RawShaderMaterial
  private readonly paletteTexture: DataTexture
  private readonly quad: Mesh
  private readonly geometry: PlaneGeometry

  constructor(palette: Palette) {
    this.paletteTexture = buildPaletteTexture(palette)
    this.material = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        tScene: { value: null as Texture | null },
        tPalette: { value: this.paletteTexture },
        uPaletteSize: { value: palette.size },
        // One palette step's worth of perturbation. Larger reads as noise;
        // smaller stops breaking up the bands the quantizer creates.
        uDitherAmount: { value: 0.075 },
        uEnabled: { value: 1 },
        uResolution: { value: new Vector2(1, 1) },
      },
      depthTest: false,
      depthWrite: false,
    })
    this.geometry = new PlaneGeometry(2, 2)
    this.quad = new Mesh(this.geometry, this.material)
    this.quad.frustumCulled = false
    this.scene.add(this.quad)
  }

  setInput(texture: Texture): void {
    this.material.uniforms.tScene.value = texture
  }

  setEnabled(on: boolean): void {
    this.material.uniforms.uEnabled.value = on ? 1 : 0
  }


  setDitherAmount(amount: number): void {
    this.material.uniforms.uDitherAmount.value = amount
  }

  dispose(): void {
    this.geometry.dispose()
    this.material.dispose()
    this.paletteTexture.dispose()
    this.scene.remove(this.quad)
  }
}

/** The palette as a 1-row float texture, sampled by `texelFetch`. */
export function buildPaletteTexture(palette: Palette): DataTexture {
  const data = new Float32Array(palette.size * 4)
  for (let i = 0; i < palette.size; i++) {
    data[i * 4] = palette.colors[i * 3] / 255
    data[i * 4 + 1] = palette.colors[i * 3 + 1] / 255
    data[i * 4 + 2] = palette.colors[i * 3 + 2] / 255
    data[i * 4 + 3] = 1
  }
  const tex = new DataTexture(data, palette.size, 1, RGBAFormat, FloatType)
  tex.minFilter = NearestFilter
  tex.magFilter = NearestFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  return tex
}

/**
 * CPU reference implementation of the shader, used by tests and by the
 * headless capture path. Keeping the two in one file makes it obvious when
 * they drift.
 */
export function quantizePixel(
  palette: Palette,
  r: number,
  g: number,
  b: number,
  px: number,
  py: number,
  ditherAmount = 0.075,
): [number, number, number] {
  const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5]
  const t = BAYER[(py & 3) * 4 + (px & 3)] / 16 - 0.46875
  const d = t * ditherAmount * 255
  const index = palette.nearest(clampByte(r + d), clampByte(g + d), clampByte(b + d))
  return palette.rgb(index)
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v)
}
